import { AxiosError } from 'axios';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import AddIcon from '@mui/icons-material/Add';
import { GridRowParams, GridRowsProp } from '@mui/x-data-grid';

import Filters from './Filters';
import CategoriesTable from './CategoriesTable';
import CategoryDetails from './CategoryDetails';
import ConfirmActionDialog from '../shared/ConfirmActionDialog';

import SS from '../shared/styles';
import useSnackbar from '../../hooks/useSnackbar';
import { IFilterState } from '../../types/categories';
import { IFilter, SearchParams } from '../../types/common';
import { deleteCategory, getCategories } from '../../requests/categories';

interface ICategoriesState {
  content: GridRowsProp;
  totalElements: number;
}

const initialFilters: IFilterState = {
  name: '',
};

const initialDialogData = {
  id: '',
  name: '',
  open: false,
  isLoading: false,
  callback: () => {},
};

function Categories() {
  const { showSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const params = useParams<{ id: string }>();
  const filtersVisibility = useState(false);
  const [loading, setLoading] = useState(false);
  const [dialogData, setDialogData] = useState(initialDialogData);
  const [filters, setFilters] = useState<IFilterState>(initialFilters);
  const [{ page, pageSize, filterFields }, setSearchParams] = useState<SearchParams>({
    page: 0,
    pageSize: 10,
    filterFields: [],
  });
  const [categories, setCategories] = useState<ICategoriesState>({ content: [], totalElements: 0 });

  useEffect(() => {
    fetchCategories();
    // eslint-disable-next-line
  }, [page, pageSize, filterFields]);

  function fetchCategories() {
    setLoading(true);
    getCategories({ page, pageSize, ...(filterFields?.length && { filterFields }) })
      .then((res) => {
        setLoading(false);
        setCategories({ content: res.data.content, totalElements: res.data.totalElements });
      })
      .catch((error: AxiosError) => {
        const errorMessage =
          (error?.response?.data as any)?.message || 'An error occurred. Please try again.';
        showSnackbar({ severity: 'error', message: errorMessage });
      });
  }

  function handlePageChange(page: number) {
    setSearchParams({ page, pageSize, filterFields });
  }

  function handlePageSizeChange(pageSize: number) {
    setSearchParams({ page: 0, pageSize, filterFields });
  }

  function handleApplyFilters() {
    const newFilters: IFilter[] = [];
    Object.entries(filters).forEach(([filterKey, filterValue]) => {
      if (filterValue) {
        newFilters.push({
          key: filterKey,
          searchOperation: 'like',
          value: filterValue,
        });
      }
    });
    setCategories({ content: [], totalElements: 0 });
    setSearchParams({ page: 0, pageSize, filterFields: newFilters });
  }

  function handleClearFilters() {
    setFilters(initialFilters);
    setCategories({ content: [], totalElements: 0 });
    setSearchParams({ page: 0, pageSize, filterFields: [] });
  }

  function handleDialogOpen(id: string, name: string, callback: () => void) {
    setDialogData({ id, name, callback, open: true, isLoading: false });
  }

  function handleDialogClose() {
    setDialogData(initialDialogData);
  }

  function handleDeleteCategory() {
    const { id, callback } = dialogData;
    if (!id) {
      showSnackbar({ severity: 'error', message: 'Unable to delete category' });
      return;
    }

    setDialogData({ ...dialogData, isLoading: true });
    deleteCategory({ _id: id })
      .then(() => {
        showSnackbar({
          severity: 'success',
          message: 'Category deleted successfully',
        });
        callback();
        setDialogData(initialDialogData);
      })
      .catch((error: AxiosError) => {
        setDialogData({ ...dialogData, isLoading: false });
        const errorMessage =
          (error?.response?.data as any)?.message || 'An error occurred. Please try again.';
        showSnackbar({ severity: 'error', message: errorMessage });
      });
  }

  function handleRowClick(params: GridRowParams) {
    navigate(`/categories/${window.btoa(params.row._id)}`);
  }

  function getMode() {
    return params.id === 'create' ? 'create' : 'update';
  }

  function getId() {
    if (getMode() === 'create') {
      return '';
    }

    const id = params.id || '';
    try {
      return window.atob(id);
    } catch (error) {
      return id;
    }
  }

  return (
    <>
      {!params.id ? (
        <div>
          <Box
            sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}
          >
            <SS.PageTitle variant="h6">Categories</SS.PageTitle>
            <Button
              component={Link}
              to="/categories/create"
              color="primary"
              startIcon={<AddIcon />}
            >
              Create
            </Button>
          </Box>
          <Filters
            visibility={filtersVisibility}
            filters={[filters, setFilters]}
            onApplyFilters={handleApplyFilters}
            onClearFilters={handleClearFilters}
          />
          <CategoriesTable
            page={page}
            pageSize={pageSize}
            rows={categories.content}
            totalElements={categories.totalElements}
            loading={loading}
            fetchCategories={fetchCategories}
            onPageChange={handlePageChange}
            onPageSizeChange={handlePageSizeChange}
            onRowClick={handleRowClick}
            onDeleteClick={handleDialogOpen}
          />
        </div>
      ) : (
        <CategoryDetails
          mode={getMode()}
          id={getId()}
          fetchCategories={fetchCategories}
          onDeleteClick={handleDialogOpen}
        />
      )}
      <ConfirmActionDialog
        open={dialogData.open}
        isLoading={dialogData.isLoading}
        title="Delete Category"
        description={`Are you sure you want to delete this category "${dialogData.name}"?`}
        onClose={handleDialogClose}
        onConfirm={handleDeleteCategory}
      />
    </>
  );
}

export default Categories;
